<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow, parseISO, isValid } from 'date-fns';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  id: { type: Number, required: true },
  title: { type: String, default: '' },
  value: { type: Number, default: 0 },
  currency: { type: String, default: 'USD' },
  stage: { type: String, default: 'lead' },
  probability: { type: Number, default: 0 },
  companyName: { type: String, default: '' },
  userName: { type: String, default: '' },
  expectedCloseDate: { type: String, default: '' },
  updatedAt: { type: [String, Number], default: null },
  isExpanded: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle', 'showDeal']);

const { t } = useI18n();

const onClickViewDetails = () => emit('showDeal', props.id);

const displayTitle = computed(() => props.title || t('DEALS.UNTITLED'));

const stageColor = computed(() => {
  const colors = {
    lead: 'bg-n-slate-9/10 text-n-slate-11',
    qualified: 'bg-n-blue-9/10 text-n-blue-11',
    proposal: 'bg-n-amber-9/10 text-n-amber-11',
    negotiation: 'bg-n-purple-9/10 text-n-purple-11',
    closed_won: 'bg-n-teal-9/10 text-n-teal-11',
    closed_lost: 'bg-n-ruby-9/10 text-n-ruby-11',
  };
  return colors[props.stage] || colors.lead;
});

const formattedValue = computed(() => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: props.currency || 'USD',
    minimumFractionDigits: 0,
  }).format(props.value);
});

const formattedUpdatedAt = computed(() => {
  if (!props.updatedAt) return '';
  try {
    const date = typeof props.updatedAt === 'string' ? parseISO(props.updatedAt) : new Date(props.updatedAt);
    if (!isValid(date)) return '';
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (e) {
    return '';
  }
});
</script>

<template>
  <CardLayout
    layout="row"
    class="cursor-pointer transition-all duration-200 hover:outline-n-brand hover:shadow-md group/cardLayout"
    :class="{
      'outline-n-brand': isExpanded,
    }"
    @click="!isExpanded && onClickViewDetails()"
  >
    <div class="flex items-center justify-start flex-1 gap-4">
      <Avatar
        :username="displayTitle"
        class="shrink-0"
        :name="title"
        :size="48"
        hide-offline-status
        rounded-full
      />
      <div class="flex flex-col gap-0.5 flex-1 min-w-0">
        <div class="flex flex-wrap items-center gap-x-4 gap-y-1 min-w-0">
          <span class="text-base font-medium truncate text-n-slate-12">
            {{ displayTitle }}
          </span>
          <span
            :class="['px-2 py-0.5 rounded-full text-xs font-medium', stageColor]"
          >
            {{ $t(`DEALS.STAGES.${stage.toUpperCase()}`) }}
          </span>
        </div>
        <div class="flex items-center justify-between">
          <div class="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
            <span class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 truncate">
              <span class="font-medium">{{ formattedValue }}</span>
              <span v-if="probability > 0">({{ probability }}%)</span>
            </span>
            <div v-if="companyName" class="w-px h-3 bg-n-slate-6" />
            <span v-if="companyName" class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 truncate">
              <Icon icon="i-lucide-building-2" size="size-3.5 text-n-slate-11" />
              {{ companyName }}
            </span>
            <div v-if="userName" class="w-px h-3 bg-n-slate-6" />
            <span v-if="userName" class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 truncate">
              <Icon icon="i-lucide-user" size="size-3.5 text-n-slate-11" />
              {{ userName }}
            </span>
          </div>
          <div class="flex items-center gap-3">
            <span
              v-if="updatedAt"
              class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 flex-shrink-0"
            >
              {{ formattedUpdatedAt }}
            </span>
            <Button
              :label="$t('DEALS.VIEW_DETAILS')"
              variant="link"
              size="xs"
              class="opacity-0 group-hover/cardLayout:opacity-100 transition-opacity"
              @click.stop="onClickViewDetails"
            />
          </div>
        </div>
      </div>
    </div>
  </CardLayout>
</template>
